import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatChipsModule } from '@angular/material/chips';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { HeaderToolbarComponent } from '../../shared/header-toolbar/header-toolbar.component';
import { AuthService } from '../../services/auth.service';
import { QuoteService, QuoteListView } from '../../services/quote.service';
import { QuoteUtilsService } from '../../services/quote-utils.service';
import { ProducerService } from '../../services/producer.service';
import { UserInfo } from '../../models/auth.model';
import { ProducerDropdownOption, ALL_PRODUCERS_ID } from '../../models/producer.model';
import {
  Quote,
  QuoteStatus,
  QuoteStatusOption
} from '../../models/quote.model';
import { EmailQuoteModalComponent, EmailQuoteModalData } from '../../shared/email-quote-modal/email-quote-modal.component';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../shared/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-quotes',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    MatSelectModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDialogModule,
    MatInputModule,
    MatFormFieldModule,
    HeaderToolbarComponent
  ],
  templateUrl: './quotes.component.html',
  styleUrls: ['./quotes.component.scss']
})
export class QuotesComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  currentUser: UserInfo | null = null;
  quotes: Quote[] = [];
  isLoading = false;
  totalQuotes = 0;
  currentPage = 0;
  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50];

  // Filter options
  selectedStatus: QuoteStatus | '' = '';
  selectedProducerId: number = ALL_PRODUCERS_ID;
  searchText: string = '';

  // KAN-29 · Quotes | Accounts | All segmented control (default 'all').
  // 'accounts' = accepted rows (Status != Quoted); 'quotes' = open quotes.
  view: QuoteListView = 'all';

  // Expose the enum to the template (account = status !== Quoted).
  readonly QuoteStatus = QuoteStatus;

  // RxJS subjects for debouncing
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  statusOptions: QuoteStatusOption[] = [
    { value: QuoteStatus.Quoted, displayName: 'Quoted' },
    { value: QuoteStatus.Complete, displayName: 'Complete' },
    { value: QuoteStatus.Delinquent, displayName: 'Delinquent' },
    { value: QuoteStatus.InProgress, displayName: 'In Progress' },
    { value: QuoteStatus.Grace, displayName: 'Grace' },
    { value: QuoteStatus.Cancelled, displayName: 'Cancelled' }
  ];
  producerOptions: ProducerDropdownOption[] = [];
  isAdmin: boolean = false;
  isLoadingProducers: boolean = false;

  // Table columns
  displayedColumns: string[] = [
    'id',
    'customerNumber',
    'customerName',
    'producer',
    'status',
    'premium',
    'createdAt',
    'actions'
  ];

  /** The current user's own producer id — the default filter (see initDefaultProducerAndLoad). */
  myProducerId: number | null = null;
  private ownFallbackChecked = false;

  constructor(
    private authService: AuthService,
    private quoteService: QuoteService,
    private quoteUtils: QuoteUtilsService,
    private producerService: ProducerService,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.currentUser = this.authService.getCurrentUser();
    if (!this.currentUser) {
      this.router.navigate(['/login']);
      return;
    }

    // KAN-39 · The /quotes and /accounts routes point at this same component and
    // pre-set the segmented view via route data. Falls back to the internal
    // 'all' default when no view is supplied.
    const routeView = this.route.snapshot.data['view'] as QuoteListView | undefined;
    if (routeView) {
      this.view = routeView;
    }

    this.checkAdminStatus();
    this.setupSearchDebounce();
    this.initDefaultProducerAndLoad();
  }

  /**
   * Default the view to the current producer's OWN quotes/accounts. If they have none of
   * their own on first load, widen to All (handled in loadQuotes' fallback) so a new or
   * empty producer isn't staring at a blank list. Admins can still filter to other producers.
   */
  private initDefaultProducerAndLoad(): void {
    this.producerService.getCurrentProducer().subscribe({
      next: (p) => {
        this.myProducerId = p.id;
        this.selectedProducerId = p.id; // default to own
        this.loadQuotes();
      },
      error: () => {
        // No producer row (e.g. a super-admin) — just show all.
        this.selectedProducerId = ALL_PRODUCERS_ID;
        this.ownFallbackChecked = true;
        this.loadQuotes();
      }
    });
  }

  /** Status pill-box selection changed. */
  onStatusPillChange(value: QuoteStatus | ''): void {
    this.selectedStatus = value ?? '';
    this.currentPage = 0;
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.loadQuotes();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearchDebounce(): void {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(searchValue => {
      if (searchValue.length >= 3) {
        this.currentPage = 0;
        if (this.paginator) {
          this.paginator.firstPage();
        }
        this.loadQuotes();
      } else if (searchValue.length === 0) {
        // Clear search and reload
        this.currentPage = 0;
        if (this.paginator) {
          this.paginator.firstPage();
        }
        this.loadQuotes();
      }
    });
  }

  loadQuotes(): void {
    this.isLoading = true;

    const offset = this.currentPage * this.pageSize;
    const request = {
      offset: offset,
      pageSize: this.pageSize,
      view: this.view,
      status: this.selectedStatus || undefined,
      producerId: this.selectedProducerId === ALL_PRODUCERS_ID ? undefined : this.selectedProducerId,
      searchText: this.searchText && this.searchText.trim().length >= 3 ? this.searchText.trim() : undefined
    };

    this.quoteService.getQuotes(request).subscribe({
      next: (response) => {
        this.quotes = response.quotes;
        this.totalQuotes = response.totalCount;
        this.isLoading = false;

        // Default-to-own fallback (once, on first load): if the producer has none of their
        // own, widen the filter to All so they don't land on an empty list.
        if (!this.ownFallbackChecked) {
          this.ownFallbackChecked = true;
          if (response.totalCount === 0 &&
              this.myProducerId != null &&
              this.selectedProducerId === this.myProducerId) {
            this.selectedProducerId = ALL_PRODUCERS_ID;
            this.loadQuotes();
          }
        }
      },
      error: (error) => {
        console.error('Error loading quotes:', error);
        this.snackBar.open('Failed to load quotes. Please try again.', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['error']
        });
        this.isLoading = false;
      }
    });
  }

  createNewQuote(): void {
    this.router.navigate(['/quote']);
  }

  viewQuote(quote: Quote): void {
    this.router.navigate(['/view-quote', quote.id]);
  }

  editQuote(quote: Quote): void {
    // KAN-40 · The builder only edits open quotes. An accepted account has locked
    // terms and editing it is blocked server-side, so route it to its servicing
    // hub instead of the builder (defense in depth — the Edit action is already
    // hidden for account rows in the template).
    if (this.isAccount(quote)) {
      this.openAccount(quote);
      return;
    }
    this.router.navigate(['/quote', quote.id]);
  }

  emailQuote(quote: Quote): void {
    const dialogData: EmailQuoteModalData = {
      quoteId: quote.id,
      customerName: quote.insuredName,
      customerEmail: quote.email
    };

    const dialogRef = this.dialog.open(EmailQuoteModalComponent, {
      width: '520px',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'calc(100vh - 32px)',
      panelClass: 'share-quote-dialog-panel',
      data: dialogData,
      disableClose: false
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Optionally refresh the quotes list or show a success message
        console.log('Email sent successfully');
      }
    });
  }

  deleteQuote(quote: Quote): void {
    // Only allow deletion of quotes in "Quoted" status
    if (quote.status !== QuoteStatus.Quoted) {
      this.snackBar.open('Only quotes with "Quoted" status can be deleted.', 'Close', {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        panelClass: ['warning']
      });
      return;
    }

    const dialogData: ConfirmDialogData = {
      title: 'Delete Quote',
      message: `Are you sure you want to delete the quote for ${quote.insuredName}? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmColor: 'warn'
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '450px',
      data: dialogData,
      disableClose: false
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.quoteService.deleteQuote(quote.id).subscribe({
          next: () => {
            this.snackBar.open('Quote deleted successfully!', 'Close', {
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'bottom',
              panelClass: ['success']
            });
            this.loadQuotes();
          },
          error: (error) => {
            console.error('Error deleting quote:', error);
            this.snackBar.open('Failed to delete quote. Please try again.', 'Close', {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'bottom',
              panelClass: ['error']
            });
          }
        });
      }
    });
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadQuotes();
  }

  onStatusFilterChange(): void {
    this.currentPage = 0; // Reset to first page when filtering
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.loadQuotes();
  }

  /**
   * KAN-29 · Quotes | Accounts | All toggle changed — reload from the first
   * page with the new view. mat-button-toggle can emit null when a value is
   * deselected; guard against that so we never drop the current view.
   */
  onViewChange(value: QuoteListView | null): void {
    if (!value || value === this.view) {
      return;
    }
    this.view = value;
    this.currentPage = 0;
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.loadQuotes();
  }

  /** KAN-29 · An accepted account is any row that is no longer just "Quoted". */
  isAccount(quote: Quote): boolean {
    return quote.status !== QuoteStatus.Quoted;
  }

  // KAN-39 · Naming: the page presents itself as "Accounts" when the active view
  // is the accepted book, and "Quotes" otherwise (offers or the combined list).
  // Single "Quotes" surface now (quotes + accounts unified, filtered by status pills).
  get pageTitle(): string {
    return 'Quotes';
  }

  get pageSubtitle(): string {
    return 'Build, send, and service aviation premium-finance quotes — filter by status.';
  }

  /** Lower-case noun for empty-state copy. */
  get pageNoun(): string {
    return 'quotes';
  }

  /**
   * KAN-29 · Row click routing: accounts open the account detail; open quotes
   * keep the existing view-quote behaviour.
   */
  onRowClick(quote: Quote): void {
    if (this.isAccount(quote)) {
      this.openAccount(quote);
    } else {
      this.viewQuote(quote);
    }
  }

  /** KAN-29 · Open the producer account detail view for an accepted quote. */
  openAccount(quote: Quote): void {
    this.router.navigate(['/account', quote.id]);
  }

  /** KAN-43 · An open offer whose expiry has passed. */
  isExpired(quote: Quote): boolean {
    return !!quote.isExpired;
  }

  /** KAN-43 · Re-issue an expired offer (new customer link + fresh 30-day clock). */
  reissue(quote: Quote): void {
    this.quoteService.reissueQuote(quote.id).subscribe({
      next: () => {
        this.snackBar.open('Quote re-issued — a fresh customer link is ready.', 'Close', {
          duration: 3000, horizontalPosition: 'center', verticalPosition: 'bottom', panelClass: ['success']
        });
        this.loadQuotes();
      },
      error: () => {
        this.snackBar.open('Could not re-issue the quote. Please try again.', 'Close', {
          duration: 5000, horizontalPosition: 'center', verticalPosition: 'bottom', panelClass: ['error']
        });
      }
    });
  }

  clearFilters(): void {
    this.selectedStatus = '';
    this.searchText = '';
    // Reset to the producer's own book (the default). /producers/me gives the real producer id.
    this.selectedProducerId = this.myProducerId ?? ALL_PRODUCERS_ID;
    this.currentPage = 0;
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.loadQuotes();
  }

  onSearchChange(): void {
    this.searchSubject.next(this.searchText);
  }

  onSearchEnter(): void {
    // Bypass debounce on Enter key
    if (this.searchText.trim().length >= 3 || this.searchText.trim().length === 0) {
      this.currentPage = 0;
      if (this.paginator) {
        this.paginator.firstPage();
      }
      this.loadQuotes();
    }
  }

  clearSearch(): void {
    this.searchText = '';
    this.onSearchEnter();
  }

  checkAdminStatus(): void {
    // Use JWT token to check admin status instead of API call
    const currentUser = this.authService.getCurrentUser();
    if (currentUser && currentUser.roles) {
      // Parse roles if it's a string (JSON serialized)
      let roles: string[] = [];
      if (typeof currentUser.roles === 'string') {
        try {
          roles = JSON.parse(currentUser.roles);
        } catch {
          roles = [];
        }
      } else if (Array.isArray(currentUser.roles)) {
        roles = currentUser.roles;
      }

      this.isAdmin = roles.includes('Admin');

      // Do NOT default selectedProducerId to currentUser.id: that value is the catalog
      // user id (JWT `sub`), not the producer id, so the list filtered by
      // CreatedByProducerId == <catalog id> and returned zero rows for admins. Leaving it
      // as ALL_PRODUCERS_ID makes admins see all quotes by default (dropdown = "All").
      if (this.isAdmin) {
        this.loadProducerOptions();
      }
    }
  }

  loadProducerOptions(): void {
    this.isLoadingProducers = true;
    this.producerService.getProducersForDropdown().subscribe({
      next: (options) => {
        this.producerOptions = options;
        this.isLoadingProducers = false;
      },
      error: (error) => {
        console.error('Error loading producer options:', error);
        this.snackBar.open('Failed to load producer options.', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['error']
        });
        this.isLoadingProducers = false;
      }
    });
  }

  onProducerFilterChange(): void {
    this.currentPage = 0; // Reset to first page when filtering
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.loadQuotes();
  }

  canViewQuote(status: QuoteStatus): boolean {
    return this.quoteUtils.canViewQuote(status);
  }

  canDeleteQuote(quote: Quote): boolean {
    return quote.status === QuoteStatus.Quoted;
  }

  getStatusChipColor(status: QuoteStatus): string {
    return this.quoteUtils.getStatusChipColor(status);
  }

  getStatusDisplayName(status: QuoteStatus): string {
    return this.quoteUtils.getStatusDisplayName(status);
  }

  /**
   * Presentation-only: map a QuoteStatus to its black+gold badge modifier class.
   * No data flow / logic change — drives styling only.
   */
  getStatusBadgeClass(status: QuoteStatus): string {
    switch (status) {
      case QuoteStatus.Quoted:     return 'badge--quoted';
      case QuoteStatus.InProgress: return 'badge--inprogress';
      case QuoteStatus.Complete:   return 'badge--complete';
      case QuoteStatus.Grace:      return 'badge--grace';
      case QuoteStatus.Delinquent: return 'badge--delinquent';
      case QuoteStatus.Cancelled:  return 'badge--cancelled';
      default:                     return 'badge--quoted';
    }
  }

  formatCurrency(amount: number): string {
    return this.quoteUtils.formatCurrency(amount);
  }

  formatDate(date: Date | string): string {
    return this.quoteUtils.formatDate(date);
  }

  getTotalPremium(quote: Quote): number {
    return this.quoteUtils.getTotalPremium(quote);
  }

  getUserDisplayName(): string {
    return this.authService.getUserDisplayName();
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }
}
